import { useEffect, useRef, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { BrandLogo, brandLogoWidth } from './BrandLogo';
import { frontendSettingsAtom } from '../../../atoms/FrontendSettings';
import { Brand } from '../../../interfaces/Brand/Brand';
import { useThemeColor } from '../hooks/useThemeColor';
import { getBrands } from '../../../api/Brand';
import { EditableStyleWrapper } from '../StyleEditor/EditableStyleWrapper';

export const Brands = () => {
  const settings = useRecoilValue(frontendSettingsAtom);

  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const { primary } = useThemeColor();

  useEffect(() => {
    setLoading(true);
    getBrands()
      .then((b) => setBrands(b))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (brands.length === 0 || !containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const totalBrandWidth = brandLogoWidth * brands.length;

    setShouldAnimate(totalBrandWidth > containerWidth);
  }, [brands]);

  useEffect(() => {
    if (!shouldAnimate || brands.length === 0) return () => {};

    const to = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % brands.length);
    }, 3000);

    return () => clearTimeout(to);
  }, [brands, activeIndex, shouldAnimate]);

  if (loading) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 0.5,
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <EditableStyleWrapper path='brandsWidgetSettings.titleStyle'>
          <Typography
            sx={{
              cursor: 'pointer',
              fontSize: { sx: '1.75rem', sm: '36px' },
              fontWeight: 600,
              transition: 'color 0.5s ease',
              '&:hover': {
                color: primary,
              },
              ...settings.settingsV2?.brandsWidgetSettings?.titleStyle,
            }}
          >
            Markalarımız
          </Typography>
        </EditableStyleWrapper>

        <EditableStyleWrapper path='brandsWidgetSettings.dividerStyle'>
          <Box
            sx={{
              backgroundColor: primary,
              height: '3px',
              width: '100px',
              ...settings.settingsV2?.brandsWidgetSettings?.dividerStyle,
            }}
          />
        </EditableStyleWrapper>
      </Box>

      <EditableStyleWrapper path='brandsWidgetSettings.carouselStyle'>
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: shouldAnimate ? 'start' : 'center',
            ...settings.settingsV2?.brandsWidgetSettings?.carouselStyle,
          }}
        >
          <Box
            sx={{
              width:
                brandLogoWidth *
                (loading ? 10 : brands.length * (shouldAnimate ? 2 : 1)),
              display: 'flex',
              flexDirection: 'row',
              transform: `translateX(-${brandLogoWidth * activeIndex}px)`,
              transition: 'transform 0.5s',
            }}
          >
            {(shouldAnimate ? brands.concat(brands) : brands).map(
              (brand, i) => (
                <BrandLogo
                  key={i}
                  brand={brand}
                  styleOverride={
                    settings.settingsV2?.brandsWidgetSettings?.carouselItemStyle
                  }
                  stylePath='brandsWidgetSettings.carouselItemStyle'
                />
              ),
            )}
          </Box>
        </Box>
      </EditableStyleWrapper>
    </Box>
  );
};
